import type {
  EscalationRecord,
  HumanMessage,
  UserMessage,
} from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/escalation";
import type { ThreadEscalationInfo } from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/message-merchant-support-agent";
import { useHandler } from "@redotech/react-util/hook";
import { useStore } from "@redotech/react-util/store";
import { showNotification } from "@redotech/redo-web/alert";
import { dismissNotificationByKey } from "@redotech/redo-web/notification-provider";
import { localStore } from "@redotech/web-util/storage";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import {
  toActiveEscalationSummaries,
  type EscalationContextType,
  type EscalationStatus,
} from "./escalation-context";
import { sanitizeIntercomBody } from "./intercom-body";

const ESCALATION_NOTIFICATION_KEY_PREFIX = "help-chat-escalation-human-message";
const ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH = 140;
const MAX_STORED_DISMISS_KEYS = 20;
const ESCALATION_DISMISSED_STORAGE_KEY =
  "redo.escalation-notification-dismissed";
const dismissedEscalationStore = localStore(ESCALATION_DISMISSED_STORAGE_KEY);

/**
 * Per-escalation notification key so concurrent escalations each get
 * their own toast instead of the newest overwriting the others.
 */
function escalationNotificationKey(escalationId: string | null): string {
  return escalationId
    ? `${ESCALATION_NOTIFICATION_KEY_PREFIX}:${escalationId}`
    : ESCALATION_NOTIFICATION_KEY_PREFIX;
}

/** Keys of escalation toasts currently (or recently) shown, for clear-all. */
const shownNotificationKeys = new Set<string>();

export function clearEscalationNotification(escalationId?: string) {
  if (escalationId) {
    const key = escalationNotificationKey(escalationId);
    dismissNotificationByKey(key, { callOnDismiss: false });
    shownNotificationKeys.delete(key);
    return;
  }
  dismissNotificationByKey(ESCALATION_NOTIFICATION_KEY_PREFIX, {
    callOnDismiss: false,
  });
  for (const key of shownNotificationKeys) {
    dismissNotificationByKey(key, { callOnDismiss: false });
  }
  shownNotificationKeys.clear();
}

/**
 * Per-message dismissal key. Dismissing a toast stores this key so we
 * only silence THAT reply — a newer reply yields a different key and
 * toasts again. (The old code keyed on escalationId alone, which
 * permanently muted every future reply once one toast was dismissed.)
 * Timestamps are normalized to epoch ms so the live SSE path (seconds)
 * and the server's `lastAdminMessageAt` (ms) produce the same key for
 * the same message.
 */
function toastDismissKey(
  escalationId: string,
  lastAdminMessageAtMs: number | null,
): string {
  return `${escalationId}:${lastAdminMessageAtMs ?? "none"}`;
}

/**
 * The dismissed store holds a JSON array of dismiss keys (one slot per
 * concurrent escalation). Legacy values were a single bare key string.
 */
function parseDismissedKeys(stored: string | undefined | null): string[] {
  if (!stored) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      Array.isArray(parsed) &&
      parsed.every((k): k is string => typeof k === "string")
    ) {
      return parsed;
    }
  } catch {
    // Legacy single-key value — fall through.
  }
  return [stored];
}

function addDismissedKey(
  stored: string | undefined | null,
  key: string,
): string {
  const keys = parseDismissedKeys(stored).filter((k) => k !== key);
  keys.push(key);
  return JSON.stringify(keys.slice(-MAX_STORED_DISMISS_KEYS));
}

interface EscalationThread {
  _id: string;
  escalation?: ThreadEscalationInfo;
}

interface ActiveEscalationCheck {
  escalationId: string;
  triageThreadId: string;
  status: EscalationStatus;
  lastAdminMessageAt: number | null;
  lastReadAt: number | null;
  hasUnread: boolean;
}

interface UseEscalationNotificationsParams<T extends EscalationThread> {
  isOpen: boolean;
  open: () => void;
  threads: readonly T[];
  escalationContext: EscalationContextType;
  merchantRpcClient: {
    checkActiveEscalation: (params: {}) => Promise<{
      activeEscalation: ActiveEscalationCheck | null;
      activeEscalations: ActiveEscalationCheck[];
    }>;
    getEscalation: (params: {
      escalationId: string;
    }) => Promise<{ escalation: EscalationRecord | null }>;
    getEscalationMessages: (params: {
      escalationId: string;
    }) => Promise<{
      status: string;
      messages: HumanMessage[];
      userMessages: UserMessage[];
    }>;
  };
  /**
   * Agent-specific thread loader. The hook needs to fetch the
   * candidate thread when the user clicks the toast — agents differ
   * in which RPC backs the list, so it's injected.
   */
  getThreads: (params: { userEmail: string }) => Promise<{ threads: T[] }>;
  loadThreadIntoChat: (threadId: string, thread: T) => Promise<void>;
  userEmail: string | undefined;
  hasInitializedRef: MutableRefObject<boolean>;
  pendingThreadLoadInFlightRef: MutableRefObject<boolean>;
  /**
   * Clear every escalation toast when the chat surface opens (default).
   * Multi-escalation callers pass false and clear per-thread instead.
   */
  clearToastsOnOpen?: boolean;
}

export function useEscalationNotifications<T extends EscalationThread>({
  isOpen,
  open,
  threads,
  escalationContext,
  merchantRpcClient,
  getThreads,
  loadThreadIntoChat,
  userEmail,
  hasInitializedRef,
  pendingThreadLoadInFlightRef,
  clearToastsOnOpen = true,
}: UseEscalationNotificationsParams<T>) {
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [pendingThreadToOpen, setPendingThreadToOpen] = useState<string | null>(
    null,
  );
  // Keyed (not one-shot boolean) so an effect re-run can't duplicate a
  // toast, but a NEWER reply detected later still fires one.
  const toastedDismissKeysRef = useRef(new Set<string>());
  // dismissKey -> escalationId for on-screen toasts (cross-tab dismissal).
  const shownToastsRef = useRef(new Map<string, string>());
  const [dismissedEscalationId, setDismissedEscalationId] = useStore(
    dismissedEscalationStore,
  );

  const { setEscalation, setTriageThreadId, setActiveEscalations } =
    escalationContext;

  const openEscalatedThreadFromNotification = useHandler(
    (threadId: string, escalationId?: string) => {
      hasInitializedRef.current = true;
      // Scoped when the caller knows the escalation, so clicking one
      // toast doesn't dismiss another escalation's toast.
      clearEscalationNotification(escalationId);
      setIsLoadingThread(true);
      setPendingThreadToOpen(threadId);
      open();
    },
  );

  const showEscalationNotification = useHandler(
    ({
      title,
      description,
      escalationId,
      triageThreadId,
      dismissKey,
    }: {
      title: string;
      description: string;
      escalationId: string | null;
      triageThreadId: string;
      // Per-message key from `toastDismissKey`. Stored on dismiss so we
      // only silence THIS reply; a newer reply re-toasts.
      dismissKey: string | null;
    }) => {
      // Replace only this escalation's toast — other concurrent
      // escalations' toasts must survive.
      clearEscalationNotification(escalationId ?? undefined);
      const notificationKey = escalationNotificationKey(escalationId);
      shownNotificationKeys.add(notificationKey);
      if (dismissKey && escalationId) {
        shownToastsRef.current.set(dismissKey, escalationId);
      }
      showNotification({
        notificationKey,
        title,
        description,
        primaryButton: {
          text: "Open chat",
          onClick: () =>
            openEscalatedThreadFromNotification(
              triageThreadId,
              escalationId ?? undefined,
            ),
        },
        onDismiss: () => {
          if (dismissKey) {
            shownToastsRef.current.delete(dismissKey);
            // Functional update reads the store fresh, merging with
            // other tabs' dismissals instead of clobbering them.
            setDismissedEscalationId((prev) =>
              addDismissedKey(prev, dismissKey),
            );
          }
        },
      });
    },
  );

  // `storage` fires when ANOTHER tab writes the dismissal store: sync local
  // state and drop any on-screen toast whose reply was dismissed there.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== ESCALATION_DISMISSED_STORAGE_KEY) {
        return;
      }
      const raw = event.newValue ?? undefined;
      setDismissedEscalationId(raw);
      const dismissedKeys = parseDismissedKeys(raw);
      for (const [dismissKey, escalationId] of shownToastsRef.current) {
        if (dismissedKeys.includes(dismissKey)) {
          shownToastsRef.current.delete(dismissKey);
          clearEscalationNotification(escalationId);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [setDismissedEscalationId]);

  /**
   * Toast for a live human reply while the chat surface is closed;
   * routes to the escalation's bound thread, not the floating setter.
   */
  const notifyClosedEscalationMessage = useHandler(
    (
      message: HumanMessage,
      // Multi-escalation callers pass explicit routing; help-chat falls
      // back to the bound state, then the floating value.
      routing?: { escalationId: string; triageThreadId: string },
    ) => {
      const threadId =
        routing?.triageThreadId ??
        escalationContext.escalation.triageThreadId ??
        escalationContext.triageThreadId;
      if (!threadId) {
        return;
      }

      const singleLineMessage = sanitizeIntercomBody(message.body);
      const description =
        singleLineMessage.length > 0
          ? singleLineMessage.length >
            ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH
            ? `${singleLineMessage.slice(0, ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH - 1)}...`
            : singleLineMessage
          : "Support sent a new message";

      const escalationId =
        routing?.escalationId ?? escalationContext.escalation.escalationId;
      // createdAt is epoch SECONDS; normalize to ms so the dismiss key
      // matches the server's `lastAdminMessageAt` for the same reply.
      const dismissKey = escalationId
        ? toastDismissKey(escalationId, Math.round(message.createdAt * 1000))
        : null;

      showEscalationNotification({
        title: `${message.authorName?.split(" ")[0] || "Support"} sent you a message`,
        description,
        escalationId,
        triageThreadId: threadId,
        dismissKey,
      });
    },
  );

  // Recover escalation state when panel is closed and context was cleared.
  // `escalationContext.escalation.escalationId` is in this effect's deps AND
  // gets mutated by `setEscalation` below — any `await` after that mutation
  // would yield to React, fire cleanup (cancelled = true), and silently
  // skip the notification. So: all awaits up front, mutate + show last.
  useEffect(() => {
    if (isOpen) {
      return;
    }
    if (escalationContext.escalation.escalationId) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const checkResult = await merchantRpcClient.checkActiveEscalation({});
        // Older servers only return the selected escalation.
        const activeEscalations =
          checkResult.activeEscalations ??
          (checkResult.activeEscalation ? [checkResult.activeEscalation] : []);
        const primary = checkResult.activeEscalation ?? activeEscalations[0];

        if (cancelled || !primary) {
          return;
        }

        const { escalation: fullEscalation } =
          await merchantRpcClient.getEscalation({
            escalationId: primary.escalationId,
          });

        if (cancelled || !fullEscalation) {
          return;
        }

        // Toast per unread-reply/waiting escalation, skipping dismissed keys.
        // State ∪ fresh store read: state alone misses other tabs' dismissals.
        const dismissedKeys = new Set([
          ...parseDismissedKeys(dismissedEscalationId),
          ...parseDismissedKeys(dismissedEscalationStore.get()),
        ]);
        const toastable = activeEscalations.filter((active) => {
          const isUnreadReply =
            active.status === "human-active" && active.hasUnread;
          const isWaiting = active.status === "waiting";
          const dismissKey = toastDismissKey(
            active.escalationId,
            active.lastAdminMessageAt,
          );
          return (
            (isUnreadReply || isWaiting) &&
            !dismissedKeys.has(dismissKey) &&
            !toastedDismissKeysRef.current.has(dismissKey)
          );
        });

        const lastAgentMessages = new Map<string, HumanMessage>();
        for (const active of toastable) {
          if (active.status !== "human-active") {
            continue;
          }
          try {
            const result = await merchantRpcClient.getEscalationMessages({
              escalationId: active.escalationId,
            });
            if (cancelled) {
              return;
            }
            if (result.messages.length > 0) {
              lastAgentMessages.set(
                active.escalationId,
                result.messages.reduce((latest, msg) =>
                  msg.createdAt > latest.createdAt ? msg : latest,
                ),
              );
            }
          } catch {
            // Fall through to generic "Support agent responded" copy.
          }
        }
        if (cancelled) {
          return;
        }

        setActiveEscalations(toActiveEscalationSummaries(activeEscalations));
        setTriageThreadId(primary.triageThreadId);
        setEscalation({
          escalationId: primary.escalationId,
          noticeTopic: fullEscalation.noticeTopic,
          status: primary.status,
          intercomConversationId: fullEscalation.intercomConversationId,
          // The bound triageThreadId is what the layout-level watcher
          // uses to route live human messages to the right cached chat.
          triageThreadId: primary.triageThreadId,
          lastAdminMessageAt: primary.lastAdminMessageAt,
          lastReadAt: primary.lastReadAt,
        });

        for (const active of toastable) {
          const dismissKey = toastDismissKey(
            active.escalationId,
            active.lastAdminMessageAt,
          );
          toastedDismissKeysRef.current.add(dismissKey);
          const lastAgentMessage = lastAgentMessages.get(active.escalationId);
          if (active.status === "waiting") {
            showEscalationNotification({
              title: "Our team is looking into this",
              description: "We'll respond shortly.",
              escalationId: active.escalationId,
              triageThreadId: active.triageThreadId,
              dismissKey,
            });
          } else if (lastAgentMessage) {
            const authorName =
              lastAgentMessage.authorName?.split(" ")[0] || "Support";
            const body = sanitizeIntercomBody(lastAgentMessage.body);
            const description =
              body.length > ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH
                ? `${body.slice(0, ESCALATION_NOTIFICATION_PREVIEW_MAX_LENGTH - 1)}...`
                : body || "Sent you a message";

            showEscalationNotification({
              title: `${authorName} sent you a message`,
              description,
              escalationId: active.escalationId,
              triageThreadId: active.triageThreadId,
              dismissKey,
            });
          } else {
            showEscalationNotification({
              title: "Support agent responded",
              description: "A support agent has replied to your escalation",
              escalationId: active.escalationId,
              triageThreadId: active.triageThreadId,
              dismissKey,
            });
          }
        }
      } catch (err) {
        // Server check failed — don't restore stale context. Log so a
        // silent recovery failure shows up in DevTools.
        console.error("[escalation] notification recovery failed", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    escalationContext.escalation.escalationId,
    setTriageThreadId,
    setEscalation,
    setActiveEscalations,
    dismissedEscalationId,
    showEscalationNotification,
    merchantRpcClient,
  ]);

  // Handle pending thread open (from notification click)
  useEffect(() => {
    if (!isOpen || !pendingThreadToOpen) {
      return;
    }
    if (pendingThreadLoadInFlightRef.current) {
      return;
    }

    const loadPendingThread = async () => {
      pendingThreadLoadInFlightRef.current = true;
      if (!userEmail) {
        setPendingThreadToOpen(null);
        pendingThreadLoadInFlightRef.current = false;
        return;
      }
      try {
        const cachedThread = threads.find((t) => t._id === pendingThreadToOpen);
        if (cachedThread) {
          await loadThreadIntoChat(pendingThreadToOpen, cachedThread);
          return;
        }

        const latestThreads = await getThreads({ userEmail });
        const freshThread = latestThreads.threads.find(
          (t) => t._id === pendingThreadToOpen,
        );
        if (freshThread) {
          await loadThreadIntoChat(pendingThreadToOpen, freshThread);
        } else {
          setIsLoadingThread(false);
        }
      } finally {
        setPendingThreadToOpen(null);
        pendingThreadLoadInFlightRef.current = false;
      }
    };

    void loadPendingThread();
  }, [
    isOpen,
    pendingThreadToOpen,
    threads,
    loadThreadIntoChat,
    merchantRpcClient,
    userEmail,
    getThreads,
    pendingThreadLoadInFlightRef,
  ]);

  // Clear notifications when panel opens
  useEffect(() => {
    if (isOpen && clearToastsOnOpen) {
      shownToastsRef.current.clear();
      clearEscalationNotification();
    }
  }, [isOpen, clearToastsOnOpen]);

  return {
    isLoadingThread,
    setIsLoadingThread,
    openEscalatedThreadFromNotification,
    notifyClosedEscalationMessage,
    dismissedEscalationId,
    setDismissedEscalationId,
  };
}
